import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { isModalOpen, setToastMessage } from '../redux/FlowReducer';
import { Box, Typography, Card, CardContent, TextField, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import VariableAutocomplete from '../VariableAutocomplete';
import PanelShell from './PanelShell';

const ProgramCallPanel = ({ nodeType, nodeId, data, onChange }) => {
  const dispatch = useDispatch();
  const vars = useSelector((state) => state.flow.vars) || [];
  const tags = useSelector((state) => state.flow.tags) || [];
  const localVars = useSelector((state) => state.flow.localVars) || [];
  const codes = useSelector((state) => state.flow.codes) || [];

  const [selectedCodeId, setSelectedCodeId] = useState('');
  const [programName, setProgramName] = useState('');
  const [focusedInput, setFocusedInput] = useState(null);
  const [inputVariables, setInputVariables] = useState([]);
  const [outputVariables, setOutputVariables] = useState([]);
  const [inputParamCount, setInputParamCount] = useState(0);
  const [outputParamCount, setOutputParamCount] = useState(0);

  // Parse variable string to get proper variable object
  const parseVariableString = (value, inputType) => {
    if (!value) return null;

    if (inputType === 'constant' || inputType === 'text') {
      return {
        variableId: value,
        variableName: value,
        variableType: 'constant'
      };
    }

    if (value.startsWith('lv_')) {
      const varName = value.substring(3);
      const foundLocalVar = localVars.find(v => v.name === varName);
      return {
        variableId: value,
        variableName: foundLocalVar ? foundLocalVar.name : varName,
        variableType: 'LV'
      };
    }

    if (value.startsWith('var_')) {
      const varId = value.substring(4);
      const foundVar = vars.find(v => {
        const varIdValue = v.varId !== undefined ? v.varId : v.varID;
        return String(varIdValue) === String(varId);
      });
      return {
        variableId: value,
        variableName: foundVar ? foundVar.varName : value,
        variableType: 'V'
      };
    }

    if (value.startsWith('tag_')) {
      const tagId = value.substring(4);
      const foundTag = tags.find(t => {
        const tagIdValue = t.tagId !== undefined ? t.tagId : t.tagID;
        return String(tagIdValue) === String(tagId);
      });
      return {
        variableId: value,
        variableName: foundTag ? foundTag.tagName : value,
        variableType: 'T'
      };
    }

    return {
      variableId: value,
      variableName: value,
      variableType: 'constant'
    };
  };

  // Initialize data when component mounts or when data prop changes
  useEffect(() => {
    if (!data) return;

    console.log('Loading ProgramCall Data:', data);

    setSelectedCodeId(data.codeId || '');
    setProgramName(data.programName || '');

    // Initialize from saved data
    if (data.inputVariables && Array.isArray(data.inputVariables)) {
      setInputVariables(data.inputVariables);
      setInputParamCount(data.inputVariables.length);
    } else if (data.ElementDetail) {
      // Load from ElementDetail format (backward compatibility)
      const inputs = data.ElementDetail
        .filter(ed => ed.parameterType === 'Input')
        .map(ed => ({
          name: ed.parameterName,
          mappedVariableId: ed.elementDetailVariableId,
          mappedVariableName: parseVariableString(ed.elementDetailVariableId, ed.elementDetailVariableType)?.variableName,
          mappedVariableType: ed.elementDetailVariableType
        }));
      setInputVariables(inputs);
      setInputParamCount(inputs.length);
    }

    if (data.outputVariables && Array.isArray(data.outputVariables)) {
      setOutputVariables(data.outputVariables);
      setOutputParamCount(data.outputVariables.length);
    } else if (data.ElementDetail) {
      // Load from ElementDetail format (backward compatibility)
      const outputs = data.ElementDetail
        .filter(ed => ed.parameterType === 'Output')
        .map(ed => ({
          name: ed.parameterName,
          mappedVariableId: ed.elementDetailVariableId,
          mappedVariableName: parseVariableString(ed.elementDetailVariableId, ed.elementDetailVariableType)?.variableName,
          mappedVariableType: ed.elementDetailVariableType
        }));
      setOutputVariables(outputs);
      setOutputParamCount(outputs.length);
    }
  }, [data, vars, tags, localVars]);

  const getColorForType = (type) => {
    switch(type) {
      case 'V': return '#fff3e0';
      case 'T': return '#edbc45';
      case 'LV': return '#f3e5f5';
      case 'constant': return '#e0e0e0';
      default: return '#f5f5f5';
    }
  };

  const handleVariableSelect = (event, variable) => {
    if (!focusedInput || !variable) return;

    console.log('Selected Variable:', variable);

    let variableId;
    let variableName;
    const variableType = variable.type;

    switch (variableType) {
      case 'LV':
        variableId = `lv_${variable.name}`;
        variableName = variable.name;
        break;
      case 'V':
        const varIdValue = variable.varID || variable.varId || variable.id;
        variableId = `var_${varIdValue}`;
        variableName = variable.varName || variable.name;
        break;
      case 'T':
        const tagIdValue = variable.tagID || variable.tagId || variable.id;
        variableId = `tag_${tagIdValue}`;
        variableName = variable.tagName || variable.name;
        break;
      case 'constant':
        variableId = variable.name;
        variableName = variable.name;
        break;
      default:
        variableId = variable.id;
        variableName = variable.name;
    }

    // Update the appropriate list
    if (focusedInput.type === 'input') {
      const updatedInputs = [...inputVariables];
      updatedInputs[focusedInput.index] = {
        ...updatedInputs[focusedInput.index],
        mappedVariableId: variableId,
        mappedVariableName: variableName,
        mappedVariableType: variableType
      };
      setInputVariables(updatedInputs);
    } else if (focusedInput.type === 'output') {
      const updatedOutputs = [...outputVariables];
      updatedOutputs[focusedInput.index] = {
        ...updatedOutputs[focusedInput.index],
        mappedVariableId: variableId,
        mappedVariableName: variableName,
        mappedVariableType: variableType
      };
      setOutputVariables(updatedOutputs);
    }

    setFocusedInput(null);
  };

  const handleClearVariable = (type, index, e) => {
    e.stopPropagation();

    if (type === 'input') {
      const updatedInputs = [...inputVariables];
      updatedInputs[index] = {
        ...updatedInputs[index],
        mappedVariableId: null,
        mappedVariableName: null,
        mappedVariableType: null
      };
      setInputVariables(updatedInputs);
    } else {
      const updatedOutputs = [...outputVariables];
      updatedOutputs[index] = {
        ...updatedOutputs[index],
        mappedVariableId: null,
        mappedVariableName: null,
        mappedVariableType: null
      };
      setOutputVariables(updatedOutputs);
    }
  };

  const handleAddInputParam = () => {
    setInputVariables([...inputVariables, {
      name: `param${inputParamCount}`,
      mappedVariableId: null,
      mappedVariableName: null,
      mappedVariableType: null
    }]);
    setInputParamCount(inputParamCount + 1);
  };

  const handleAddOutputParam = () => {
    setOutputVariables([...outputVariables, {
      name: `output${outputParamCount}`,
      mappedVariableId: null,
      mappedVariableName: null,
      mappedVariableType: null
    }]);
    setOutputParamCount(outputParamCount + 1);
  };

  const handleRemoveParam = (type, index) => {
    if (type === 'input') {
      setInputVariables(inputVariables.filter((_, i) => i !== index));
    } else {
      setOutputVariables(outputVariables.filter((_, i) => i !== index));
    }
  };

  const handleParamNameChange = (type, index, newName) => {
    if (type === 'input') {
      const updatedInputs = [...inputVariables];
      updatedInputs[index] = { ...updatedInputs[index], name: newName };
      setInputVariables(updatedInputs);
    } else {
      const updatedOutputs = [...outputVariables];
      updatedOutputs[index] = { ...updatedOutputs[index], name: newName };
      setOutputVariables(updatedOutputs);
    }
  };

  const InputCard = ({ label, variable, index, type }) => {
    const isFocused = focusedInput?.type === type && focusedInput?.index === index;

    return (
      <Card
        onClick={() => setFocusedInput({ type, index })}
        sx={{
          cursor: 'pointer',
          border: isFocused ? '2px solid #6a1b9a' : '1px solid #ddd',
          backgroundColor: isFocused ? '#f3e5f5' : 'white',
          mb: 1
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <TextField
              size="small"
              value={label}
              onChange={(e) => {
                e.stopPropagation();
                handleParamNameChange(type, index, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              placeholder="Parameter name"
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  padding: '4px 8px'
                }
              }}
            />
            <Button
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveParam(type, index);
              }}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              ×
            </Button>
          </Box>

          {variable?.mappedVariableName ? (
            <Box sx={{
              p: 1,
              backgroundColor: getColorForType(variable.mappedVariableType),
              borderRadius: 1,
              fontSize: '0.9rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{variable.mappedVariableName}</span>
              <Button
                size="small"
                onClick={(e) => handleClearVariable(type, index, e)}
                sx={{ minWidth: 'auto', p: 0.5, fontSize: '1rem' }}
              >
                ×
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              Değişken seçilmedi
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleSave = () => {
    if (!selectedCodeId && !programName.trim()) {
      dispatch(setToastMessage({
        type: 'error',
        message: 'Lütfen bir kod seçin veya program adını girin.'
      }));
      return;
    }

    // Get selected code name if code is selected
    const selectedCode = codes.find(c => c.id === selectedCodeId);
    const finalProgramName = selectedCode ? selectedCode.name : programName;

    // Create ElementDetail entries for input and output variables
    const elementDetails = [];

    // Add input variables to ElementDetail
    inputVariables.forEach(input => {
      if (input.mappedVariableId) {
        elementDetails.push({
          elementDetailVariableId: input.mappedVariableId,
          elementDetailVariableType: input.mappedVariableType,
          parameterName: input.name,
          parameterType: "Input"
        });
      }
    });

    // Add output variables to ElementDetail
    outputVariables.forEach(output => {
      if (output.mappedVariableId) {
        elementDetails.push({
          elementDetailVariableId: output.mappedVariableId,
          elementDetailVariableType: output.mappedVariableType,
          parameterName: output.name,
          parameterType: "Output"
        });
      }
    });

    // Build params object for JSON format (fallback)
    const params = {};
    inputVariables.forEach(input => {
      if (input.mappedVariableId) {
        params[input.name] = input.mappedVariableName || input.mappedVariableId;
      }
    });

    const title = `${finalProgramName}()`;

    const nodeData = {
      codeId: selectedCodeId || null,
      programName: finalProgramName,
      inputVariables,
      outputVariables,
      params, // JSON params as fallback
      ElementDetail: elementDetails,
      label: 'ProgramCall',
      Title: title,
    };

    console.log('Saving ProgramCall Node Data:', nodeData);

    onChange(nodeData);
    dispatch(isModalOpen());
    dispatch(setToastMessage({
      type: 'success',
      message: 'Program çağrısı kaydedildi.'
    }));
  };

  if (nodeType !== "ProgramCall") return null;

  // Handle code selection
  const handleCodeChange = (e) => {
    const codeId = e.target.value;
    setSelectedCodeId(codeId);
    if (codeId) {
      const code = codes.find(c => c.id === codeId);
      if (code) {
        setProgramName(code.name);
      }
    }
  };

  return (
    <PanelShell title="Program Çağır" onSave={handleSave}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Code Selection */}
        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Kod Seçimi
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Kod Listesi</InputLabel>
            <Select
              value={selectedCodeId}
              label="Kod Listesi"
              onChange={handleCodeChange}
            >
              <MenuItem value="">
                <em>-- Kod seçin --</em>
              </MenuItem>
              {codes.map(code => (
                <MenuItem key={code.id} value={code.id}>
                  {code.name} {code.language ? `(${code.language})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Program Name (manual entry or from code) */}
        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Program Adı
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={programName}
            onChange={(e) => {
              setProgramName(e.target.value);
              if (e.target.value !== codes.find(c => c.id === selectedCodeId)?.name) {
                setSelectedCodeId(''); // Clear code selection if name is manually changed
              }
            }}
            placeholder="ör: CalculateCOP"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Left Panel - Parameters */}
          <Box sx={{ flex: 1 }}>
            {/* Input Parameters */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Input Parametreleri
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleAddInputParam}
                  sx={{ fontSize: '0.75rem' }}
                >
                  + Ekle
                </Button>
              </Box>

              {inputVariables.length === 0 ? (
                <Typography variant="body2" color="text.disabled" fontStyle="italic" sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  Henüz input parametresi eklenmedi
                </Typography>
              ) : (
                inputVariables.map((input, index) => (
                  <InputCard
                    key={index}
                    label={input.name}
                    variable={input}
                    index={index}
                    type="input"
                  />
                ))
              )}
            </Box>

            {/* Output Parameters */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Output Parametreleri
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleAddOutputParam}
                  sx={{ fontSize: '0.75rem' }}
                >
                  + Ekle
                </Button>
              </Box>

              {outputVariables.length === 0 ? (
                <Typography variant="body2" color="text.disabled" fontStyle="italic" sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  Henüz output parametresi eklenmedi
                </Typography>
              ) : (
                outputVariables.map((output, index) => (
                  <InputCard
                    key={index}
                    label={output.name}
                    variable={output}
                    index={index}
                    type="output"
                  />
                ))
              )}
            </Box>

            {focusedInput && (
              <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                Sağdaki listeden bir değişken veya sabit değer seçin
              </Typography>
            )}
          </Box>

          {/* Right Panel - Variable Selector */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
              Değişken / Sabit Seçimi
            </Typography>
            <VariableAutocomplete
              value={null}
              onChange={handleVariableSelect}
              placeholder="Tag, değişken veya sabit ara..."
              showConstants={true}
            />

            {/* Preview */}
            <Card sx={{ mt: 2, backgroundColor: '#f3e5f5', border: '1px solid #6a1b9a' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" fontWeight={600} color="#6a1b9a" mb={0.5}>
                  Önizleme
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {programName || 'ProgramName'}(
                  {inputVariables.filter(v => v.mappedVariableId).map(v => v.mappedVariableName).join(', ') || '...'}
                  )
                </Typography>
                {outputVariables.filter(v => v.mappedVariableId).length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Returns: {outputVariables.filter(v => v.mappedVariableId).map(v => v.mappedVariableName).join(', ')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </PanelShell>
  );
};

export default ProgramCallPanel;
